import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import { useAsyncPaginatedCatalog } from '../../../../hooks/useAsyncPaginatedCatalog';
import { fetchActiveSuppliersPage } from '../../../../utils/catalogPageFetchers';

export interface SupplierSelectorProps {
  /** Habilita la carga paginada de proveedores (solo cuando el panel está abierto en este paso). */
  isActive: boolean;
  /**
   * Proveedor conocido (registro en edición o última selección) para mostrar su nombre aunque
   * no esté en la página cargada, p. ej. al volver del paso 2 (el selector se desmonta).
   */
  editPin?: { id: number; name: string } | null;
  selectedSupplierId: string;
  /** Muestra error en rojo solo tras validación (p. ej. guardar borrador sin proveedor). */
  showError?: boolean;
  onChange: (supplierId: string, supplier: { id: number; name: string } | null) => void;
  disabled?: boolean;
}

const SupplierSelector: React.FC<SupplierSelectorProps> = ({
  isActive,
  editPin = null,
  selectedSupplierId,
  showError = false,
  onChange,
  disabled = false,
}) => {
  const supplierPins = useMemo(() => {
    const id = Number(selectedSupplierId);
    if (!id || Number.isNaN(id)) return [];
    if (editPin && editPin.id === id) {
      return [{ id: editPin.id, name: editPin.name }];
    }
    return [{ id, name: `Proveedor #${id}` }];
  }, [selectedSupplierId, editPin]);

  const supplierCatalog = useAsyncPaginatedCatalog({
    isActive,
    fetchPage: fetchActiveSuppliersPage,
    pageSize: 10,
    pinItems: supplierPins,
  });

  const selectedSupplier =
    supplierCatalog.displayOptions.find((s) => s.id.toString() === selectedSupplierId) || null;

  const isSupplierMissing = !selectedSupplierId || selectedSupplierId === '';
  const showSupplierFieldError = showError && isSupplierMissing && !disabled;

  return (
    <Box sx={{ maxWidth: '420px', marginBottom: '18px' }}>
      <Autocomplete
        options={supplierCatalog.displayOptions}
        filterOptions={supplierCatalog.filterOptions}
        loading={supplierCatalog.loading}
        openOnFocus
        onOpen={supplierCatalog.onOpen}
        onInputChange={supplierCatalog.onInputChange}
        getOptionLabel={(option) => option.name}
        value={selectedSupplier || null}
        onChange={(_, newValue) => {
          onChange(
            newValue ? newValue.id.toString() : '',
            newValue ? { id: newValue.id, name: newValue.name } : null,
          );
        }}
        disabled={disabled}
        ListboxProps={supplierCatalog.listboxProps}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Proveedor"
            placeholder="Seleccionar proveedor"
            required
            error={showSupplierFieldError}
            helperText={showSupplierFieldError ? 'Debe seleccionar un proveedor' : ''}
          />
        )}
        noOptionsText="No hay proveedores disponibles"
      />
    </Box>
  );
};

export default SupplierSelector;
